import classnames from 'classnames';
import {
  type ComponentType,
  type FunctionComponent,
  type KeyboardEvent,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
} from 'react';

import { Cancel as IconCancel } from '@/core/tamagoshi/icons/Cancel';

import styles from './Chip.module.scss';

export interface ChipProps extends PropsWithChildren {
  id?: string;
  className?: string;
  closable?: boolean;
  disabled?: boolean;
  icon?: ReactNode;
  selected?: boolean;
  readOnly?: boolean;
  theme?: 'default' | 'outline';
  tag?: ComponentType<{ disabled?: boolean }> | keyof JSX.IntrinsicElements;
  onClick?: (event: KeyboardEvent | MouseEvent) => unknown;
  onClose?: (event: KeyboardEvent | MouseEvent) => unknown;
  onKeyDown?: (event: KeyboardEvent) => unknown;
}

export const Chip: FunctionComponent<ChipProps> = ({
  id,
  className,
  closable,
  disabled,
  icon,
  selected,
  readOnly,
  theme = 'default',
  tag: Tag = 'button',
  onClick,
  onClose,
  onKeyDown,
  children,
  ...restProps
}) => {
  const rootClassnames = classnames(
    styles.chip,
    {
      [styles.default]: theme === 'default',
      [styles.outline]: theme === 'outline',
      [styles.selected]: selected,
      [styles.readOnly]: readOnly,
      [styles.disabled]: disabled,
    },
    className,
  );

  const handleClick = (event: MouseEvent) => {
    if (readOnly || disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    if (onClick) {
      onClick(event);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (readOnly || disabled) {
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    if (onKeyDown) {
      onKeyDown(event);
    }
  };

  const handleCloseKeyDown = (event: KeyboardEvent) => {
    if (closable && onClose && event.key === 'Backspace') {
      onClose(event);
    }
  };

  return (
    <Tag
      id={id}
      className={rootClassnames}
      disabled={disabled}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      {...restProps}
    >
      {icon && (
        <span className={styles.icon} data-testid="icon-chip">
          {icon}
        </span>
      )}
      {children}
      {closable && (
        <span
          className={styles.closeButton}
          onClick={onClose}
          onKeyDown={handleCloseKeyDown}
          data-testid="closable-chip"
          role="button"
          tabIndex={0}
        >
          <IconCancel className={styles.closeButtonIcon} />
        </span>
      )}
    </Tag>
  );
};

Chip.displayName = 'Chip';
